import Dexie, { type Table } from 'dexie';
import type { BabyRecord, EventRecord, LocalBaby, LocalEvent } from '@/lib/schemas';

export type OutboxEntity = 'event' | 'baby';
export type OutboxOpType = 'create' | 'update' | 'delete';
export type OutboxStatus = 'pending' | 'inflight' | 'conflict' | 'error';

/**
 * A durable, immutable-ID sync operation. Written in the same IndexedDB
 * transaction as the entity change it describes.
 */
export interface OutboxOp {
  seq?: number; // local causal order (auto-increment)
  operation_id: string;
  household_id: string;
  entity: OutboxEntity;
  entity_id: string;
  op: OutboxOpType;
  /** Server version this op expects to modify (0 for create). */
  base_version: number;
  /** Full proposed record (server shape). */
  data: EventRecord | BabyRecord;
  client_ts: string;
  retry_count: number;
  status: OutboxStatus;
  /** True once the op has been sent at least once; then it is never coalesced. */
  attempted: boolean;
  next_attempt_at: number;
  last_error?: string | null;
  /** On conflict: the server's current record for manual resolution. */
  server_record?: EventRecord | BabyRecord | null;
  rejection_reason?: string | null;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export interface ImportBatch {
  id: string;
  household_id: string;
  baby_id: string;
  filename: string;
  file_hash: string;
  started_at: string;
  finished_at: string;
  accepted: number;
  skipped: number;
  rejected: number;
}

export type PredictionType = 'feed' | 'sleep_start' | 'wake';

/** Logged prediction; prediction fields are never rewritten after creation. */
export interface PredictionLog {
  id: string; // deterministic: baby|type|basis_event_id|model_version
  baby_id: string;
  prediction_type: PredictionType;
  model_version: string;
  generated_at: string;
  generated_ms: number;
  basis_event_id: string;
  sample_size: number;
  window_start: string;
  window_end: string;
  point: string;
  observed_at: string | null;
  error_minutes: number | null;
  within_window: boolean | null;
}

export class KaiDB extends Dexie {
  events!: Table<LocalEvent, string>;
  babies!: Table<LocalBaby, string>;
  outbox!: Table<OutboxOp, number>;
  meta!: Table<MetaRow, string>;
  importBatches!: Table<ImportBatch, string>;
  predictionLog!: Table<PredictionLog, string>;

  constructor(name = 'kai-baby-tracker') {
    super(name);
    this.version(1).stores({
      events:
        'id, household_id, baby_id, [baby_id+started_ms], [baby_id+type+started_ms], sync_state, [baby_id+source_system+source_key]',
      babies: 'id, household_id',
      outbox: '++seq, &operation_id, entity_id, status, household_id',
      meta: 'key',
      importBatches: 'id, baby_id, file_hash',
      predictionLog: 'id, [baby_id+prediction_type+generated_ms], basis_event_id',
    });
  }
}

export let db = new KaiDB();

/** Test hook: swap in a fresh database instance. */
export function __setDb(next: KaiDB) {
  db = next;
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row === undefined ? fallback : (row.value as T);
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export const META = {
  household: 'household', // LocalHousehold
  activeBabyId: 'activeBabyId',
  deviceId: 'deviceId',
  session: 'session', // { userId, email } snapshot for offline display
  syncCursorPrefix: 'syncCursor:', // + household_id
  lastSyncAt: 'lastSyncAt',
  lastSyncError: 'lastSyncError',
  settings: 'settings',
  localContext: 'localContext',
  authGeneration: 'authGeneration',
} as const;

export interface LocalHousehold {
  id: string;
  name: string;
  /** Whether this household exists on the server and should sync. */
  cloud: boolean;
  role?: 'owner' | 'caregiver';
  /** Account that validated membership; never itself grants remote access. */
  accountId?: string;
  /** True only after a validated delta hydration for this selected workspace. */
  syncReady?: boolean;
}
